// =====================================================================
// src/modulos/garagem.js — três telas debaixo de um nome
//
// A 0032 construiu a garagem inteira no banco em 17/09 e nenhuma tela foi
// feita. Ao desenhá-las apareceram dois buracos que só a tela encontra: o
// banco não sabia devolver o mapa nem listar placas (0037), e o
// proprietário não tinha o módulo no menu (0039).
//
// DE ONDE VEIO O DESENHO
//
// `renderGaragem` do Monteiro (index.html, 8853): o `h2.page-title` com o
// ícone, a barra de ações logo abaixo, o `.card` com `overflow-x:auto`, o
// `h3` com a contagem entre parênteses, a `table.table-cards` com
// `data-label` em cada `<td>`, a linha clicável, e a frase cinza centrada
// quando não há nada. A disponibilidade por período veio de
// `_renderDisponibilidadeGaragem` (5771).
//
// ONDE DIVERGE, E POR QUÊ
//
//   · Lá é UMA tela: o gestor aluga uma vaga por diária. Aqui são TRÊS
//     perfis com telas que quase não se parecem — guarita, imobiliária e
//     balcão —, porque a garagem do Garvey é de vagas fixas. Até a 0100 as
//     vagas eram dos donos e a segunda tela era a do proprietário; na 0101
//     (Hugo, 02/11/2026) vagas e placas passaram a ser da IMOBILIÁRIA, e a
//     tela de dono virou a dela — o esboço da 0099, parte 1: "As vagas e
//     os carros da <imobiliária>. Quem libera é o hotel.", Minhas vagas,
//     Placas, + Declarar vaga, + Declarar placa. É a aba Garagem do módulo
//     `vaga_garagem` (main.js). O hotel vê "Imobiliária" onde via
//     "Proprietário".
//
//   · O MAPA não existe lá. Veio da reunião de 18/09: "só a visão de no
//     mapa vai ter o mapa. Hoje é no papelzinho". É a grade por andar da
//     governança (`.grade-quartos`/`.grade-quarto`), que a equipe já lê de
//     relance — faixa por setor em vez de faixa por andar.
//
//   · A guarita é uma tela de UM campo. Não tem menu de abas, não tem
//     lista, não tem tabela: é um computador numa guarita de porta aberta,
//     com alguém de pé e um carro na rampa.
//
// QUEM VÊ O QUÊ NÃO ESTÁ AQUI. Está no RLS e nas funções da 0032/0037.
// `visaoDe()` escolhe o DESENHO; se ela errasse, o banco continuaria
// recusando — tela some com um inspetor aberto.
// =====================================================================
import { el, icone, abrirModal, fecharModal, aviso, confirmar, enquantoAtualiza, barraDeAbas } from '../ui.js';
import { visaoDe, podeDeclarar, textoDaImobiliaria, agruparPorSetor, estadoDaVaga, resumoDoMapa,
         normalizarPlaca, placaParecePronta, validarPeriodo, respostaDaGuarita,
         linhaDaVaga, CORES_DO_QUADRO, LEGENDA_DO_QUADRO } from '../lib/garagem.js';
import { visaoDe as visaoDeVisitas, situacaoDaVisita, textoDaJanela } from '../lib/visitas.js';
import * as Q from '../lib/quadro.js';
import { somarDias } from '../lib/reservas.js';
import { hojeDe } from '../lib/contexto.js';
import { barraDoQuadro } from './barra_quadro.js';
import { modalReservaDeVaga } from './reserva_de_vaga.js';

export function montar(cont, { auth, contexto }) {
  const visao = visaoDe(contexto);
  let vivo = true;
  let vagas = [], placas = [], reservas = [];
  // Mapa × Quadro (Hugo, 23/09/2026). A guarita não tem abas de garagem.
  let aba = 'mapa';
  const quadro = { estado: null, chave: null, reservas: null };
  const hoje = () => hojeDe(contexto, Date.now());   // o dia do HOTEL (0020)

  function refazer() { return enquantoAtualiza(async () => { await carregar(); desenhar(); }); }

  async function carregar() {
    if (visao === 'guarita') return;          // a guarita não lista nada
    const pedidos = [auth.rpc('vagas_do_cliente'), auth.rpc('placas_do_cliente')];
    if (visao === 'balcao') pedidos.push(auth.rpc('reservas_da_garagem'));
    const r = await Promise.all(pedidos);
    vagas = r[0] || []; placas = r[1] || []; reservas = r[2] || [];
  }

  // ── A GUARITA ──────────────────────────────────────────────────────
  // Um campo e uma resposta. A resposta ocupa a tela inteira de propósito:
  // quem lê está de pé, de longe, com o carro esperando.
  //
  // 0045: "A guarita vê a visita numa aba ao lado" (Hugo, 20/09). A aba
  // só existe quando o hotel contratou visitas e o porteiro as vê; a tela
  // de um campo continua sendo a que abre.
  let abaDaGuarita = 'placa';
  const guaritaVeVisitas = visaoDeVisitas(contexto) !== null;

  function telaDaGuarita() {
    cont.textContent = '';
    cont.dataset.tela = 'garagem';
    cont.dataset.visao = 'guarita';
    if (guaritaVeVisitas) {
      cont.dataset.aba = abaDaGuarita;
      cont.appendChild(barraDeAbas('guarita-abas',
        [{ chave: 'placa', rotulo: 'Placa' }, { chave: 'visitas', rotulo: 'Visitas de hoje' }],
        abaDaGuarita, ch => { abaDaGuarita = ch; telaDaGuarita(); }));
      if (abaDaGuarita === 'visitas') return visitasDaGuarita();
    }
    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('div', { style: { fontSize: '13px', color: '#6b7280' } },
        'Digite a placa do carro que está na rampa.')));

    const campo = el('input', {
      class: 'input', id: 'guarita-placa', type: 'text', autocomplete: 'off',
      placeholder: 'ABC1D23', maxlength: '8',
      style: { fontSize: '30px', textAlign: 'center', letterSpacing: '3px', fontWeight: '700', padding: '14px' },
      oninput: ev => {
        // Normaliza enquanto digita, do jeito que o banco vai procurar.
        const pos = ev.target.selectionStart;
        ev.target.value = normalizarPlaca(ev.target.value);
        ev.target.setSelectionRange(pos, pos);
        botao.disabled = !placaParecePronta(ev.target.value);
      },
      onkeydown: ev => { if (ev.key === 'Enter') consultar(); },
    });
    const botao = el('button', { type: 'button', class: 'btn btn-primary', id: 'guarita-consultar',
      disabled: true, style: { width: '100%', fontSize: '17px', minHeight: '52px' },
      onclick: () => consultar() }, 'Consultar');
    const resposta = el('div', { id: 'guarita-resposta' });

    cont.appendChild(el('div', { class: 'card', style: { maxWidth: '520px' } }, campo,
      el('div', { style: { height: '12px' } }), botao));
    cont.appendChild(resposta);

    async function consultar() {
      const placa = normalizarPlaca(campo.value);
      if (!placa) return;
      botao.disabled = true;
      let r = null;
      try { const lista = await auth.rpc('garagem_por_placa', { p_placa: placa }); r = lista && lista[0]; }
      catch (e) { aviso(e.message, 'erro'); botao.disabled = false; return; }
      if (!vivo) return;
      const d = respostaDaGuarita(r);
      resposta.textContent = '';
      resposta.appendChild(el('div', { id: 'guarita-veredito', class: 'guarita-veredito',
        dataset: { autorizado: d.autorizado ? '1' : '0' },
        style: { borderColor: d.cor, color: d.cor } },
        el('div', { class: 'placa' }, placa),
        el('div', { class: 'titulo' }, d.titulo),
        el('div', { class: 'detalhe' }, d.detalhe),
        d.ate ? el('div', { class: 'detalhe' }, `Até ${dataCurta(d.ate)}`) : null));
      botao.disabled = false;
      campo.select();
    }
    setTimeout(() => campo.focus(), 0);
  }

  // As visitas de hoje, para o porteiro saber quem esperar. Só leitura:
  // a chegada é da recepção (0045).
  function visitasDaGuarita() {
    const alvo = el('div', { id: 'guarita-visitas' }, el('p', { class: 'vazio' }, 'Carregando…'));
    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('div', { style: { fontSize: '13px', color: '#6b7280' } },
        'Quem o prédio espera hoje. A placa cadastrada abre pela consulta; sem placa, o fornecedor vai à recepção.')));
    cont.appendChild(alvo);
    auth.rpc('visitas_do_dia', {}).then(lista => {
      if (!vivo || abaDaGuarita !== 'visitas') return;
      const visitas = lista || [];
      alvo.textContent = '';
      alvo.appendChild(el('div', { class: 'card', style: { overflowX: 'auto' } },
        el('h3', { class: 'secao-titulo' }, `Visitas de hoje (${visitas.length})`),
        el('table', { class: 'table-cards', id: 'tabela-visitas-guarita' },
          el('thead', {}, el('tr', {}, el('th', {}, 'Horário'), el('th', {}, 'Fornecedor'), el('th', {}, 'Destino'), el('th', {}, 'Placa'), el('th', {}, 'Situação'))),
          el('tbody', {}, visitas.length ? visitas.map(v => {
            const s = situacaoDaVisita(v);
            return el('tr', { dataset: { visita: v.id, situacao: s.chave } },
              el('td', { 'data-label': 'Horário' }, textoDaJanela(v.janela_de, v.janela_ate)),
              el('td', { 'data-label': 'Fornecedor', style: { fontWeight: '700' } }, v.fornecedor),
              el('td', { 'data-label': 'Destino' }, v.unidade || 'Prédio'),
              el('td', { 'data-label': 'Placa', style: { letterSpacing: '1px', fontWeight: '700' } }, v.placa || el('span', { style: { color: '#9ca3af', fontWeight: '400' } }, 'sem carro')),
              el('td', { 'data-label': 'Situação' }, el('span', { class: 'estado ' + s.classe }, s.rotulo)));
          }) : el('tr', {}, el('td', { colspan: 5, style: { textAlign: 'center', color: '#9ca3af', padding: '20px' } }, 'Nenhuma visita esperada hoje.'))))));
    }).catch(e => { if (vivo) aviso(e.message, 'erro'); });
  }

  // ── O MAPA (balcão) ────────────────────────────────────────────────
  function faixaDoMapa(faixa) {
    return el('div', { class: 'grade-andar', dataset: { setor: faixa.setor } },
      el('div', { class: 'andar-titulo' }, faixa.setor,
        el('span', { class: 'quem' }, ` · ${faixa.vagas.length} vaga(s)`)),
      el('div', { class: 'grade-quartos' }, faixa.vagas.map(v => {
        const e = estadoDaVaga(v);
        return el('button', { type: 'button', class: 'grade-quarto vaga-' + e.chave,
          dataset: { vaga: v.identificacao, estado: e.chave },
          title: `${v.identificacao} — ${e.rotulo}${visao === 'balcao' && v.imobiliaria_nome ? ' · ' + v.imobiliaria_nome : ''}`,
          // situar e liberar são do hotel: a imobiliária só olha as dela
          onclick: visao === 'balcao' ? () => abrirVaga(v) : null }, v.identificacao);
      })));
  }

  function abrirVaga(v) {
    const e = estadoDaVaga(v);
    const corpo = el('div', {},
      el('div', { class: 'modal-title' }, `Vaga ${v.identificacao}`),
      el('div', { style: { fontSize: '12px', color: '#6b7280', marginBottom: '10px' } },
        v.imobiliaria_nome ? `Declarada por ${v.imobiliaria_nome}` : 'Sem imobiliária'),
      el('div', { class: 'ficha-mov', style: { borderColor: e.cor, color: e.cor } }, e.rotulo,
        v.ocupada_hoje && v.placa_hoje
          ? el('span', { style: { color: '#6b7280' } }, ` · ${v.placa_hoje}${v.ocupante ? ' · ' + v.ocupante : ''}`)
          : null),
      el('div', { class: 'form-row' },
        el('div', {}, el('label', { class: 'label' }, 'Setor'),
          el('input', { class: 'input', id: 'vaga-setor', value: v.setor || '', placeholder: 'Subsolo 1' })),
        el('div', {}, el('label', { class: 'label' }, 'Ordem'),
          el('input', { class: 'input', id: 'vaga-ordem', type: 'number', min: '0', max: '9999', value: v.ordem == null ? '' : v.ordem }))),
      el('div', { style: { fontSize: '12px', color: '#6b7280', marginTop: '8px' } },
        'O setor e a ordem são do mapa: onde a vaga fica no chão.'));

    const acoes = el('div', { class: 'acoes' },
      v.situacao === 'pendente'
        ? el('button', { type: 'button', class: 'btn btn-verde', id: 'btn-liberar-vaga',
            onclick: async () => {
              const ok = await confirmar({ titulo: `Liberar a vaga ${v.identificacao}?`,
                texto: 'Depois de liberada, ela passa a valer na guarita.', sim: 'Liberar' });
              if (!ok) return;
              try { await auth.rpc('liberar_vaga', { p_vaga: v.id }); } catch (e2) { aviso(e2.message, 'erro'); return; }
              fecharModal(); await refazer(); aviso(`Vaga ${v.identificacao} liberada.`);
            } }, 'Liberar vaga')
        : null,
      el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-situar',
        onclick: async () => {
          const setor = document.getElementById('vaga-setor').value;
          const ordemTexto = document.getElementById('vaga-ordem').value;
          try {
            await auth.rpc('situar_vaga', { p_vaga: v.id, p_setor: setor,
              p_ordem: ordemTexto === '' ? null : Number(ordemTexto) });
          } catch (e2) { aviso(e2.message, 'erro'); return; }
          fecharModal(); await refazer(); aviso('Mapa atualizado.');
        } }, 'Salvar no mapa'));
    corpo.appendChild(acoes);
    abrirModal(corpo);
  }

  // ── AS PLACAS ──────────────────────────────────────────────────────
  function tabelaDePlacas() {
    const podeLiberar = visao === 'balcao';
    const corpo = el('tbody', {}, placas.length ? placas.map(p =>
      el('tr', { dataset: { placa: p.placa } },
        el('td', { 'data-label': 'Placa', style: { fontWeight: '700', letterSpacing: '1px' } }, p.placa),
        el('td', { 'data-label': 'Apelido' }, p.apelido || '—'),
        podeLiberar ? el('td', { 'data-label': 'Imobiliária' }, p.imobiliaria_nome || '—') : null,
        el('td', { 'data-label': 'Situação' },
          el('span', { class: 'estado ' + (p.situacao === 'liberada' ? 'est-liberado' : p.situacao === 'pendente' ? 'est-a_limpar' : 'est-sem_movimento') },
            p.situacao === 'liberada' ? 'Liberada' : p.situacao === 'pendente' ? 'Aguardando' : 'Inativa')),
        podeLiberar && p.situacao === 'pendente'
          ? el('td', { 'data-label': '' }, el('button', { type: 'button', class: 'btn btn-verde btn-sm',
              dataset: { liberarPlaca: p.placa },
              onclick: async () => {
                try { await auth.rpc('liberar_placa', { p_placa_id: p.id }); } catch (e) { aviso(e.message, 'erro'); return; }
                await refazer(); aviso(`Placa ${p.placa} liberada.`);
              } }, 'Liberar'))
          : (podeLiberar ? el('td', {}) : null)))
      : el('tr', {}, el('td', { colspan: podeLiberar ? 5 : 3,
          style: { textAlign: 'center', color: '#9ca3af', padding: '20px' } },
          'Nenhuma placa declarada ainda.')));

    return el('div', { class: 'card', style: { overflowX: 'auto' } },
      el('h3', { class: 'secao-titulo' }, `Placas (${placas.length})`),
      el('table', { class: 'table-cards', id: 'tabela-placas' },
        el('thead', {}, el('tr', {},
          el('th', {}, 'Placa'), el('th', {}, 'Apelido'),
          podeLiberar ? el('th', {}, 'Imobiliária') : null,
          el('th', {}, 'Situação'), podeLiberar ? el('th', {}) : null)),
        corpo));
  }

  // ── DECLARAR (imobiliária, 0101) ───────────────────────────────────
  function modalDeclararVaga() {
    const corpo = el('div', {},
      el('div', { class: 'modal-title' }, 'Declarar vaga'),
      el('div', { style: { fontSize: '12px', color: '#6b7280', marginBottom: '10px' } },
        'A vaga passa a valer depois que o hotel liberar.'),
      // 0055: no hotel que numera as vagas (Garvey: 1 a 52) só entra o número
      // dentro da faixa — quem recusa, com a faixa na mensagem, é o banco
      el('label', { class: 'label' }, 'Número da vaga'),
      el('input', { class: 'input', id: 'nova-vaga', placeholder: 'Ex.: 14', autofocus: true }),
      el('div', { class: 'acoes' },
        el('button', { type: 'button', class: 'btn btn-secondary', onclick: () => fecharModal() }, 'Cancelar'),
        el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-declarar-vaga',
          onclick: async () => {
            const ident = document.getElementById('nova-vaga').value.trim();
            if (!ident) return;
            try { await auth.rpc('declarar_vaga', { p_identificacao: ident }); }
            catch (e) { aviso(e.message, 'erro'); return; }
            fecharModal(); await refazer(); aviso('Vaga declarada. Aguarde a liberação.');
          } }, 'Declarar')));
    abrirModal(corpo);
  }

  function modalDeclararPlaca() {
    const corpo = el('div', {},
      el('div', { class: 'modal-title' }, 'Declarar placa'),
      el('div', { style: { fontSize: '12px', color: '#6b7280', marginBottom: '10px' } },
        'Sem liberar, a guarita não reconhece o carro.'),
      el('label', { class: 'label' }, 'Placa'),
      el('input', { class: 'input', id: 'nova-placa', placeholder: 'ABC1D23', maxlength: '8',
        style: { letterSpacing: '2px', fontWeight: '700' },
        oninput: ev => { ev.target.value = normalizarPlaca(ev.target.value); } }),
      el('label', { class: 'label' }, 'Apelido (opcional)'),
      el('input', { class: 'input', id: 'nova-placa-apelido', placeholder: 'o carro da Maria' }),
      el('div', { class: 'acoes' },
        el('button', { type: 'button', class: 'btn btn-secondary', onclick: () => fecharModal() }, 'Cancelar'),
        el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-declarar-placa',
          onclick: async () => {
            const placa = normalizarPlaca(document.getElementById('nova-placa').value);
            if (!placa) return;
            try {
              await auth.rpc('declarar_placa', { p_placa: placa,
                p_apelido: document.getElementById('nova-placa-apelido').value.trim() || null });
            } catch (e) { aviso(e.message, 'erro'); return; }
            fecharModal(); await refazer(); aviso('Placa declarada. Aguarde a liberação.');
          } }, 'Declarar')));
    abrirModal(corpo);
  }

  // ── RESERVAR E CANCELAR (0097 — 6A, 2A) ───────────────────────────
  // O mesmo modal de três portas (modulos/reserva_de_vaga.js). Daqui o
  // balcão escolhe a reserva: `reservas_para_vaga` dá quarto, titular e
  // período sem ler a tabela de reservas — a Administração edita a
  // Garagem sem ter o módulo Reservas.
  async function abrirReservarVaga({ de = null, vagaId = null } = {}) {
    const ini = de || hoje();
    let opcoes;
    try { opcoes = (await auth.rpc('reservas_para_vaga', { p_de: ini, p_ate: de ? de : somarDias(ini, 30) })) || []; }
    catch (e) { aviso(e.message, 'erro'); return; }
    modalReservaDeVaga({ auth, opcoes, de, vagaId, aoSalvar: () => { quadro.reservas = null; aba === 'quadro' ? recarregarQuadro() : refazer(); } });
  }
  async function cancelarVaga(r) {
    const sim = await confirmar({ titulo: `Cancelar a vaga ${r.vaga}?`, texto: `${r.unidade ? 'Apto ' + r.unidade + ' · ' : ''}${dataCurta(r.de)} → ${dataCurta(r.ate)}. A vaga fica livre para outra reserva.`, sim: 'Cancelar vaga', nao: 'Voltar', perigoso: true });
    if (!sim) return;
    try { await auth.rpc('cancelar_vaga', { p_pedido: r.id, p_motivo: null }); } catch (e) { aviso(e.message, 'erro'); return; }
    aviso(`Vaga ${r.vaga} cancelada.`); quadro.reservas = null; refazer();
  }

  // ── AS RESERVAS (balcão) ───────────────────────────────────────────
  function tabelaDeReservas() {
    const corpo = el('tbody', {}, reservas.length ? reservas.map(r =>
      el('tr', { dataset: { reserva: r.id, vaga: r.vaga } },
        el('td', { 'data-label': 'Período' }, `${dataCurta(r.de)} → ${dataCurta(r.ate)}`),
        el('td', { 'data-label': 'Vaga', style: { fontWeight: '700' } }, r.vaga),
        el('td', { 'data-label': 'Apto' }, r.unidade || '—'),
        el('td', { 'data-label': 'Hóspede' }, r.hospede || '—'),
        el('td', { 'data-label': 'Placa' }, r.placa || el('span', { style: { color: '#9ca3af' } }, 'no check-in')),
        el('td', { 'data-label': 'Situação' },
          el('span', { class: 'estado ' + (r.situacao === 'cancelada' ? 'est-sem_movimento' : r.liberada_em ? 'est-liberado' : 'est-aguardando_inspecao') },
            r.situacao === 'cancelada' ? 'Cancelada' : r.liberada_em ? 'No prédio' : 'Aguarda check-in')),
        // 0097: liberada não se cancela (o banco recusa: encerra no check-out)
        el('td', { 'data-label': '' }, r.situacao === 'confirmada' && !r.liberada_em
          ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { cancelarVaga: r.id }, onclick: () => cancelarVaga(r) }, 'Cancelar vaga') : null)))
      : el('tr', {}, el('td', { colspan: 7, style: { textAlign: 'center', color: '#9ca3af', padding: '20px' } },
          'Nenhuma reserva de vaga.')));

    return el('div', { class: 'card', style: { overflowX: 'auto' } },
      el('h3', { class: 'secao-titulo' }, `Reservas de vaga (${reservas.length})`),
      el('table', { class: 'table-cards', id: 'tabela-reservas-vaga' },
        el('thead', {}, el('tr', {},
          el('th', {}, 'Período'), el('th', {}, 'Vaga'), el('th', {}, 'Apto'),
          el('th', {}, 'Hóspede'), el('th', {}, 'Placa'), el('th', {}, 'Situação'), el('th', {}, ''))),
        corpo));
  }

  // ── AS TRÊS TELAS ──────────────────────────────────────────────────
  function desenhar() {
    if (!vivo) return;
    if (visao === 'guarita') return telaDaGuarita();

    cont.textContent = '';
    cont.dataset.tela = 'garagem';
    cont.dataset.visao = visao;
    cont.dataset.aba = aba;
    const imob = visao === 'imobiliaria';
    cont.appendChild(barraDeAbas('gar-telas', [{ chave: 'mapa', rotulo: 'Mapa' }, { chave: 'quadro', rotulo: 'Quadro' }], aba, ch => {
      aba = ch;
      if (aba === 'quadro' && !quadro.reservas) recarregarQuadro(); else desenhar();
    }));
    if (aba === 'quadro') return desenharQuadro();
    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('div', { style: { fontSize: '13px', color: '#6b7280' } },
        imob ? textoDaImobiliaria(contexto)
             : 'O portão fica fechado. A guarita consulta a placa antes de abrir.')));

    if (imob) {
      // com `ver vaga_garagem` ela só olha: sem botão que o banco recusaria
      if (podeDeclarar(contexto)) cont.appendChild(el('div', { class: 'usu-filtros' },
        el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-nova-vaga',
          onclick: modalDeclararVaga }, '+ Declarar vaga'),
        el('button', { type: 'button', class: 'btn btn-secondary', id: 'btn-nova-placa',
          onclick: modalDeclararPlaca }, '+ Declarar placa')));
    } else {
      const c = resumoDoMapa(vagas);
      // 0097: "+ Nova Reserva de Garagem" do MdM (renderGaragem, 8877)
      cont.appendChild(el('div', { class: 'usu-filtros' },
        el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-reservar-vaga', onclick: () => abrirReservarVaga() }, '+ Reservar vaga')));
      cont.appendChild(el('div', { class: 'contadores', id: 'contadores-garagem' },
        contador('livre', c.livre, 'LIVRES'), contador('ocupada', c.ocupada, 'OCUPADAS HOJE'),
        contador('pendente', c.pendente, 'AGUARDANDO'), contador('inativa', c.inativa, 'INATIVAS')));
    }

    // O MAPA. Para a imobiliária é a lista curta das vagas dela — e ele
    // continua sendo um mapa, com as mesmas cores, porque a vaga dela pode
    // estar ocupada por um hóspede.
    cont.appendChild(el('div', { class: 'secao-titulo' }, imob ? 'Minhas vagas' : 'Mapa da garagem'));
    const faixas = agruparPorSetor(vagas);
    if (!faixas.length) {
      cont.appendChild(el('p', { class: 'vazio' },
        imob ? 'A imobiliária ainda não declarou nenhuma vaga.' : 'Nenhuma vaga cadastrada no prédio.'));
    } else {
      const mapa = el('div', { id: 'mapa-garagem' });
      for (const f of faixas) mapa.appendChild(faixaDoMapa(f));
      cont.appendChild(mapa);
    }

    cont.appendChild(tabelaDePlacas());
    if (visao === 'balcao') cont.appendChild(tabelaDeReservas());
  }

  // ── O QUADRO: VAGA × DIA ───────────────────────────────────────────
  // A mesma matriz do Quadro de Reservas (MdM `renderQuadro`, 4874; aqui
  // em `reservas.js`): a barra azul — com o período livre no canto
  // superior esquerdo —, o cabeçalho de dias repetido por grupo, a coluna
  // fixa à esquerda. As linhas são as VAGAS, agrupadas por setor como no
  // mapa, e a cor vem de `estadoDaVagaNoDia` (lib/garagem.js), não da
  // plataforma. Quem vê quais vagas é o banco (`vagas_do_cliente`,
  // `reservas_da_garagem`): a imobiliária recebe só as dela (0101).
  const ehMovel = () => !!(globalThis.matchMedia && globalThis.matchMedia('(max-width:768px)').matches);
  const estadoDoQuadro = () => {
    if (!quadro.estado) quadro.estado = ehMovel() ? Q.janelaMovel(hoje()) : Q.inicial(hoje());
    else if (ehMovel() && !quadro.estado.inicioMovel) quadro.estado = Q.paraMovel(quadro.estado);
    else if (!ehMovel() && quadro.estado.inicioMovel) quadro.estado = Q.paraDesktop(quadro.estado);
    return quadro.estado;
  };
  async function carregarQuadro() {
    const { ini, fim, chave } = Q.intervaloDaJanela(estadoDoQuadro());
    // o intervalo do banco é FECHADO (0037: ate >= p_de e de <= p_ate)
    quadro.reservas = (await auth.rpc('reservas_da_garagem', { p_de: ini, p_ate: somarDias(fim, -1) })) || [];
    quadro.chave = chave;
  }
  function recarregarQuadro() {
    return enquantoAtualiza(async () => {
      try { await carregarQuadro(); } catch (e) { aviso(e.message, 'erro'); return; }
      desenhar();
    });
  }
  function navegarQuadro(novo) {
    quadro.estado = novo;
    const [ci, cf] = (quadro.chave || '..').split('..');
    const j = Q.intervaloDaJanela(novo);
    const cobre = quadro.chave && j.ini >= ci && j.fim <= somarDias(cf, 1);
    if (!cobre) recarregarQuadro(); else desenhar();
  }

  function desenharQuadro() {
    const e = estadoDoQuadro();
    const dias = Q.diasDaJanela(e);
    const imob = visao === 'imobiliaria';   // só para a frase do quadro vazio
    const raiz = el('div', { class: 'fade-in', id: 'gar-quadro' });
    raiz.appendChild(barraDoQuadro({ prefixo: 'gq', estado: e, hoje: hoje(), movel: ehMovel(), navegar: navegarQuadro, legenda: LEGENDA_DO_QUADRO }));

    const colFixa = { position: 'sticky', left: '0', background: '#1B3A7A', color: '#fff', fontWeight: '700', padding: '4px 10px', zIndex: '1', whiteSpace: 'nowrap', border: '1px solid rgba(255,255,255,0.15)', minWidth: '80px' };
    const cabecalho = () => {
      let mesAnt = -1;
      return el('tr', { class: 'q-datas' },
        el('td', { class: 'q-flat-col', style: { ...colFixa, fontSize: '11px', textAlign: 'center' } }, 'Vaga'),
        dias.map(iso => {
          const c = Q.cabecalhoDoDia(iso, hoje(), mesAnt); mesAnt = c.mes;
          const bg = c.hoje ? '#f59e0b' : (c.fimDeSemana ? '#f0f0f0' : '#f8fafc');
          const cor = c.hoje ? '#fff' : (c.fimDeSemana ? '#374151' : '#6b7280');
          return el('th', { dataset: c.hoje ? { dia: iso, hoje: '1' } : { dia: iso }, style: { minWidth: '48px', width: '48px', padding: '3px 1px', textAlign: 'center', background: bg, color: cor, border: '1px solid #d1d5db', fontWeight: c.hoje ? '700' : '500' } },
            c.mostraMes ? el('div', { style: { fontSize: '8px', color: cor, opacity: '0.85', fontWeight: '600' } }, c.mesCurto) : null,
            el('div', { style: { fontSize: '9px' } }, c.nome),
            el('div', { style: { fontSize: '13px', fontWeight: '700' } }, c.dia));
        }));
    };
    const linha = v => el('tr', { dataset: { vaga: v.identificacao } },
      el('td', { class: 'q-flat-col', style: { ...colFixa, fontSize: '12px' } }, 'Vaga ' + v.identificacao),
      linhaDaVaga(v, quadro.reservas || [], dias).map(c => {
        const cores = CORES_DO_QUADRO[c.chave];
        const r = c.res;
        // O nome do hóspede só vem quando o banco o manda: na 0101,
        // `reservas_da_garagem` o entrega à imobiliária só nas reservas DELA
        // — a vaga dela reservada pelo balcão para o hóspede de OUTRA
        // imobiliária chega sem nome, e a célula mostra o apartamento. O
        // corte é do banco; a tela não esconde nem inventa.
        const quem = r ? (r.hospede ? r.hospede : (r.unidade ? 'Apto ' + r.unidade : '')) : '';
        const titulo = [`Vaga ${v.identificacao}`, c.rotulo,
          r && r.unidade ? 'Apto ' + r.unidade : null, r && r.hospede ? r.hospede : null,
          r ? `${dataCurta(r.de)}→${dataCurta(r.ate)}` : null, r && r.placa ? r.placa : null].filter(Boolean).join(' | ');
        return el('td', { dataset: { dia: c.iso, estado: c.chave, reserva: r ? r.id : null }, title: titulo,
          // 0097 (2A): a célula livre do balcão abre o modal com a vaga e o dia
          onclick: r ? () => modalReservaDaVaga(v, r, c.rotulo)
                   : (visao === 'balcao' && c.chave === 'livre') ? () => abrirReservarVaga({ de: c.iso, vagaId: v.id }) : null,
          style: { minWidth: '48px', width: '48px', height: '40px', background: cores.fundo, border: '1px solid rgba(0,0,0,0.12)', cursor: (r || (visao === 'balcao' && c.chave === 'livre')) ? 'pointer' : 'default', verticalAlign: 'top', padding: '3px 2px' } },
          c.mostra ? el('div', { style: { fontSize: '9px', fontWeight: '700', color: cores.texto, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '44px' } }, quem) : null);
      }));

    const corpo = el('tbody');
    const faixas = agruparPorSetor(vagas);
    for (const f of faixas) {
      corpo.appendChild(el('tr', { class: 'q-grupo', dataset: { grupo: f.setor } },
        el('td', { colspan: String(dias.length + 1), style: { position: 'sticky', left: '0', background: '#1B3A7A', color: '#fff', fontSize: '11px', fontWeight: '800', letterSpacing: '1px', padding: '5px 12px', textTransform: 'uppercase', borderTop: '3px solid rgba(255,255,255,0.25)' } },
          `▶ ${f.setor} `, el('span', { style: { fontWeight: '400', opacity: '0.7', fontSize: '10px' } }, `(${f.vagas.length} vaga${f.vagas.length > 1 ? 's' : ''})`))));
      corpo.appendChild(cabecalho());
      for (const v of f.vagas) corpo.appendChild(linha(v));
    }
    if (!faixas.length) corpo.appendChild(el('tr', null, el('td', { style: { padding: '32px', textAlign: 'center', color: '#6b7280' } },
      imob ? 'A imobiliária ainda não declarou nenhuma vaga.' : 'Nenhuma vaga cadastrada no prédio.')));
    raiz.appendChild(el('div', { class: 'card q-table-wrap', style: { padding: '0', overflow: 'auto', maxHeight: 'calc(100vh - 200px)' } },
      el('table', { id: 'gq-tabela', style: { borderCollapse: 'collapse', fontSize: '12px', width: 'auto' } }, corpo)));
    cont.appendChild(raiz);
  }

  // O detalhe de uma reserva de vaga, só leitura. Reservar é pelo botão e
  // pela célula livre (0097); liberar, no check-in.
  function modalReservaDaVaga(v, r, rotulo) {
    const cores = CORES_DO_QUADRO[r.liberada_em ? 'ocupada' : 'reservada'];
    const linhaInfo = (k, val) => el('div', { style: { display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '6px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px' } },
      el('span', { style: { color: '#6b7280' } }, k), el('span', { style: { fontWeight: '600', color: '#111827' } }, val));
    abrirModal(el('div', { id: 'modal-reserva-vaga' },
      el('div', { class: 'modal-title' }, `Vaga ${v.identificacao}`),
      el('div', { class: 'ficha-mov', style: { borderColor: cores.fundo, color: r.liberada_em ? '#b91c1c' : '#a16207' } }, rotulo),
      linhaInfo('Período', `${dataCurta(r.de)} → ${dataCurta(r.ate)}`),
      linhaInfo('Apartamento', r.unidade || '—'),
      linhaInfo('Hóspede', r.hospede || '—'),   // 0101: nulo quando a reserva é de outra imobiliária
      linhaInfo('Placa', r.placa || 'no check-in'),
      el('div', { class: 'acoes' }, el('button', { type: 'button', class: 'btn btn-secondary', onclick: () => fecharModal() }, 'Fechar'))));
  }

  function contador(chave, n, rotulo) {
    const cor = estadoDaVaga(chave === 'ocupada' ? { situacao: 'liberada', ocupada_hoje: true }
              : chave === 'pendente' ? { situacao: 'pendente' }
              : chave === 'inativa' ? { situacao: 'inativa' } : { situacao: 'liberada' }).cor;
    return el('div', { class: 'contador', dataset: { estado: chave }, style: { borderColor: cor, color: cor } },
      el('div', { class: 'n' }, String(n)), el('div', { class: 't' }, rotulo));
  }

  function dataCurta(d) {
    if (!d) return '';
    const p = String(d).slice(0, 10).split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}` : String(d);
  }

  if (visao === null) {
    cont.textContent = '';
    cont.dataset.tela = 'garagem';
    cont.dataset.visao = 'nenhuma';
    cont.appendChild(el('p', { class: 'vazio' }, 'A garagem não está contratada para este hotel.'));
    return () => { vivo = false; };
  }

  if (visao === 'guarita') { telaDaGuarita(); }
  else { carregar().then(() => desenhar()).catch(e => { if (vivo) aviso(e.message, 'erro'); }); }

  return () => { vivo = false; };
}
